import { Link } from "react-router-dom"
import { Calendar, ChevronDown, Heart, Sparkles, CheckCircle, ArrowRight, Shield, Stethoscope } from "lucide-react"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Card, CardContent } from "../components/ui/card"

export default function WomenCare() {
  return (
    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative h-[80vh] min-h-[500px] flex items-center">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/MaternitymatersBanner.webp"
              alt="Women Care"
              className="w-full h-full object-cover brightness-50"
            />
          </div>
          <div className="container relative z-10 px-4 mx-auto text-white">
            <div className="max-w-2xl">
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                <Sparkles className="w-4 h-4 mr-2" />
                Women Care
              </Badge>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Comprehensive <span className="text-primary">Women's Health</span> Services
              </h1>
              <p className="max-w-md mt-6 text-lg text-white/90">
                Dedicated to providing exceptional healthcare for women at every stage of life with compassion and expertise.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Button size="lg" className="gap-2 group transition-all duration-300 hover:translate-y-[-2px] shadow-lg bg-white text-primary hover:bg-white/90">
                  <Calendar className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <a href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001" rel="noopener noreferrer">
                    Book an Appointment
                  </a>
                </Button>
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 flex justify-center">
            <a href="#women-care" className="flex items-center justify-center w-10 h-10 mb-8 text-white bg-primary/50 rounded-full backdrop-blur-sm animate-bounce">
              <ChevronDown className="w-6 h-6" />
              <span className="sr-only">Scroll Down</span>
            </a>
          </div>
        </section>

        {/* Women Care Highlight Section */}
        <section id="women-care" className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-10 text-center">
              <Badge className="mb-4 bg-primary/10 text-primary">
                <Heart className="w-5 h-5 mr-2" />
                Comprehensive Care
              </Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Women's <span className="text-primary">Health Services</span>
              </h2>
              <p className="max-w-3xl text-lg text-muted-foreground">
                At Pranaam Hospitals, we provide specialized care for women's unique health needs, from adolescence through menopause and beyond.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-12 sm:grid-cols-3">
              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img src="/images/ThirdTrimester.webp" alt="Gynecology Care" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Gynecology Services</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Complete gynecological care including routine checkups, PCOD/PCOS management, and menstrual health.
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img src="/images/pregnant-woman.webp" alt="Preventive Care" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Preventive Care</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Regular screenings, cancer prevention, and wellness programs designed for women's health.
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img src="/images/portrait-beautiful-pregnant-woman-1.webp" alt="Specialized Treatment" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Specialized Treatment</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Advanced treatments for fibroids, endometriosis, and other women's health conditions.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="p-6 mt-12 rounded-2xl bg-gray-100">
              <h3 className="mb-4 text-xl font-bold">Our Women's Health Services Include:</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[
                  "Routine gynecological examinations",
                  "PCOD/PCOS management and treatment",
                  "Menstrual disorder management",
                  "Menopause care and hormone therapy",
                  "Fibroid and ovarian cyst treatment",
                  "Endometriosis management",
                  "Breast health and cancer screening",
                  "Cervical cancer screening (Pap smear)",
                  "Family planning and contraception counseling",
                  "Pre-marital and pre-conception counseling",
                  "Adolescent gynecology",
                  "Minimally invasive gynecological surgeries",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center mt-10">
              <Button size="lg" className="gap-2 shadow-md" asChild>
                <a href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001" rel="noopener noreferrer">
                  Schedule Your Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-4 bg-white text-primary">
                <Stethoscope className="w-5 h-5 mr-2" />
                Specialized Services
              </Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Complete <span className="text-primary">Women's Healthcare</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: "Gynecological Care",
                  image: "/images/ThirdTrimester.webp",
                  description: "Comprehensive care for all gynecological conditions with expert diagnosis and treatment.",
                  items: ["Routine checkups", "PCOD/PCOS treatment", "Menstrual health", "Infection management"],
                },
                {
                  title: "Preventive Health",
                  image: "/images/pregnant-woman.webp",
                  description: "Proactive health screening and prevention programs for early detection and wellness.",
                  items: ["Cancer screenings", "Health assessments", "Vaccination programs", "Wellness counseling"],
                },
                {
                  title: "Surgical Care",
                  image: "/images/portrait-beautiful-pregnant-woman-1.webp",
                  description: "Advanced minimally invasive surgical procedures with faster recovery times.",
                  items: ["Laparoscopic surgery", "Hysterectomy", "Fibroid removal", "Ovarian cyst surgery"],
                },
              ].map((service, index) => (
                <div key={index} className="flex flex-col h-full bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="relative h-64">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="mb-2 text-xl font-bold">{service.title}</h3>
                    <p className="mb-4 text-muted-foreground">{service.description}</p>
                    <ul className="pl-5 mt-auto space-y-1 list-disc">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="text-sm">{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-2xl shadow-xl md:h-96">
                  <img src="/images/GeneralGynecology.webp" alt="Women's Healthcare Team" className="w-full h-full object-cover" />
                </div>
              </div>
              <div>
                <Badge className="mb-4 bg-gray-100 text-primary">
                  <Heart className="w-5 h-5 mr-2" />
                  About Our Care
                </Badge>
                <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
                  Expert Care. <span className="text-primary">Compassionate</span> Approach.
                </h2>
                <p className="mb-8 text-lg text-muted-foreground">
                  Our team of experienced gynecologists and healthcare professionals is dedicated to providing personalized care that addresses your unique health needs with sensitivity and expertise.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button className="gap-2 shadow-md" asChild>
                    <Link to="/doctors">
                      Meet Our Specialists
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
